/* import __COLOCATED_TEMPLATE__ from './follow-training.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type WorkflowConnectorInsertableActionsService from 'embercom/services/workflow-connector-insertable-actions';
import type FinUsageService from 'embercom/services/fin-usage-service';
import { inject as service } from '@ember/service';
import CustomBotListData from 'embercom/objects/operator/configuration/custom-bots/custom-bot-list-data';
import { objectTypes } from 'embercom/models/data/matching-system/matching-constants';
import { AI_AGENT_WORKFLOW_TARGET } from 'embercom/lib/operator/custom-bots/constants';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';

interface Signature {
  Args: {
    accordion: $TSFixMe;
    guidelinesCount: number;
    categoriesCount: number;
    escalationRulesCount: number;
    escalationGuidanceCount: number;
  };
}

export default class FollowTraining extends Component<Signature> {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare appService: AppService;
  @service declare workflowConnectorInsertableActions: WorkflowConnectorInsertableActionsService;
  @service declare finUsageService: FinUsageService;

  tasksData: CustomBotListData;

  constructor(owner: any, args: any) {
    super(owner, args);
    this.tasksData = new CustomBotListData(
      owner,
      this.appService.app.id,
      AI_AGENT_WORKFLOW_TARGET,
      objectTypes.triggerableCustomBot,
    );

    taskFor(this.loadTasks).perform();
    taskFor(this.loadDataConnectors).perform();
  }

  @task({ restartable: true })
  *loadDataConnectors(): TaskGenerator<void> {
    yield this.workflowConnectorInsertableActions.loadActions();
  }

  @task({ restartable: true })
  *loadTasks(): TaskGenerator<void> {
    let response = yield this.tasksData.contentSearch(0, 0);
    this.tasksData.totalCount = response.totalCount;
  }

  get totalContentCount() {
    return Object.values(this.knowledgeHubService.usageSummary || {}).reduce(
      (acc, summary) => acc + summary.agent,
      0,
    );
  }

  get tasksCount() {
    return this.tasksData.totalCount;
  }

  get dataConnectorsCount() {
    return this.workflowConnectorInsertableActions.actions.filter(
      (action) =>
        !action.isTemplate &&
        (action.finEnabled || action.usage === 'fin' || action.usage === 'fin_voice'),
    ).length;
  }

  get escalationItemsCount() {
    return this.args.escalationRulesCount + this.args.escalationGuidanceCount;
  }

  get manageIdentityRoute() {
    return this.appService.app.isStandaloneApp
      ? 'apps.app.settings.ai-automation.fin-ai-agent'
      : 'apps.app.automation.fin-ai-agent.settings';
  }

  get manageTasksRoute() {
    return this.appService.app.isStandaloneApp
      ? 'apps.app.standalone.tasks.index'
      : 'apps.app.automation.fin-ai-agent.tasks';
  }

  get manageContentRoute() {
    return 'apps.app.knowledge-hub.overview';
  }

  get manageGuidanceRoute() {
    return this.appService.app.isStandaloneApp
      ? 'apps.app.standalone.guidance'
      : 'apps.app.automation.fin-ai-agent.guidance';
  }

  get manageDataConnectorsRoute() {
    return 'apps.app.settings.app-settings.custom-actions';
  }

  get manageCategoriesRoute() {
    return this.appService.app.isStandaloneApp
      ? 'apps.app.standalone.attributes'
      : 'apps.app.automation.fin-ai-agent.attributes';
  }

  get usageLimitSubtitleKey() {
    if (this.finUsageService.isInTrial) {
      return 'operator.workflows.visual-builder.pass-to-fin.settings.follow-training.usage-limit-free-trial';
    }
    if (this.finUsageService.isFinResolutionsLimitReached) {
      return 'operator.workflows.visual-builder.pass-to-fin.settings.follow-training.usage-limit-limit-reached';
    }
    if (this.finUsageService.areUsedResolutions90PercentOfLimit) {
      return 'operator.workflows.visual-builder.pass-to-fin.settings.follow-training.usage-limit-approaching';
    }
    if (this.finUsageService.isFinUsageLimitEnabled) {
      return 'operator.workflows.visual-builder.pass-to-fin.settings.follow-training.usage-limit-set';
    }
    return 'operator.workflows.visual-builder.pass-to-fin.settings.follow-training.not-set';
  }

  get manageUsageLimitRoute() {
    return 'apps.app.settings.subscription.usage';
  }

  get manageEscalationRoute() {
    if (this.appService.app.canUseFeature('fin-escalation-rules')) {
      return this.appService.app.isStandaloneApp
        ? 'apps.app.standalone.escalation'
        : 'apps.app.automation.fin-ai-agent.escalation';
    }

    return this.appService.app.isStandaloneApp
      ? 'apps.app.standalone.guidance'
      : 'apps.app.automation.fin-ai-agent.guidance';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::Sections::FollowTraining': typeof FollowTraining;
    'workflows/graph-editor/node-items/steps/fin/sections/follow-training': typeof FollowTraining;
  }
}
